import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from './FormContext';
import render from './fieldRender';

/**
 * Field
 * @ignore
 */
class Field extends Component {
  static propTypes = {
    /**
     * Prop that should be validated. For example, the `valuePropName` of checkbox is `checked`.
     */
    valuePropName: PropTypes.string,
    /**
     * When to validate the value of children node.
     */
    validateTrigger: PropTypes.string,
    /**
     * When to collect the value of children node.
     */
    trigger: PropTypes.string,
    /**
     * The unique identifier of field, corresponding to a value in the form values.
     */
    name: PropTypes.string.isRequired,
    /**
     * @ignore
     */
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    /**
     * Callback fired after validation.
     * @param {object} result The result of validation.
     * You can pull out the return of the validator by accessing `result.promiseValue`.
     */
    onValidate: PropTypes.func,
    /**
     * Handler that format the input.
     * @param {string | boolean | number} value The value of input.
     * @returns {object} The formatted value.
     */
    formatter: PropTypes.func,
    /**
     * Render Props.
     * @param {object} props Please refer to `fieldRender`.
     * @returns {object} The React node to be rendered.
     */
    render: PropTypes.func.isRequired,
  };

  static defaultProps = {
    valuePropName: 'value',
    validateTrigger: 'onChange',
    trigger: 'onChange',
    render,
    size: 'medium',
    defaultValue: '',
  };

  constructor(props) {
    super(props);
    const { register, name } = props;
    if (!register) {
      console.warn('Field must be inside a component decorated with createForm()');
    }
    if (register && name != null) {
      register(name, this);
    }
  }

  componentDidUpdate(prevProps) {
    const { name, register, unRegister } = this.props;
    if (name !== prevProps.name && prevProps.name != null) {
      unRegister(prevProps.name);
      register(name, this);
    }
  }

  componentWillUnmount() {
    const { unRegister, name } = this.props;
    if (unRegister && name) {
      unRegister(name);
    }
  }

  getValueFromEvent(e, value, ...other) {
    let newValue;
    const { valuePropName, getValueFromEvent } = this.props;
    if (getValueFromEvent) {
      return getValueFromEvent(e, value, ...other);
    }
    if (!e) {
      return e;
    }
    if (!e.target && !e.preventDefault) {
      newValue = e;
    } else if (value != null) {
      newValue = value;
    } else if (e.target[valuePropName] != null) {
      newValue = e.target[valuePropName];
    } else {
      newValue = e.target.value;
    }
    return newValue;
  }

  format(e, oldValue, name) {
    const { formatter } = this.props;
    const value = this.getValueFromEvent(e, oldValue);
    const formattedValue = formatter ? formatter(value) : value;
    return { [name]: formattedValue };
  }

  handleChangeAndValidate = (e, value, ...args) => {
    const {
      name,
      onFieldChange,
      validateItem,
      trigger,
      onValidate,
      options,
      [trigger]: onTrigger,
    } = this.props;
    const formattedValue = this.format(e, value, name);
    onFieldChange(formattedValue);
    const result = validateItem(name, formattedValue[name], this, options);
    if (result) result.then(onValidate, onValidate);
    if (onTrigger) {
      onTrigger(e, value, ...args);
    }
    return result;
  };

  handleValueChange = (e, value, ...args) => {
    const { name, onFieldChange, trigger, [trigger]: onTrigger } = this.props;
    const formattedValue = this.format(e, value, name);
    onFieldChange(formattedValue);
    if (onTrigger) {
      onTrigger(e, value, ...args);
    }
  };

  handleValidate = (e, value, ...args) => {
    let result;
    const {
      name,
      validateItem,
      onValidate,
      options,
      validateTrigger,
      [validateTrigger]: onTrigger,
    } = this.props;
    if (!e) {
      result = validateItem(name, null, this, options);
    } else {
      const formattedValue = this.format(e, value, name);
      result = validateItem(name, formattedValue[name], this, options);
    }
    if (result) result.then(onValidate, onValidate);
    if (onTrigger) {
      onTrigger(e, value, ...args);
    }
    return result;
  };

  render() {
    const {
      defaultValue,
      values,
      errors,
      error,
      validatings,
      validating,
      name,
      render,
      validateTrigger,
      valuePropName,
      trigger,
      status: statusProps,
      ...other
    } = this.props;
    const dataBindProps = {};
    if (name != null) {
      dataBindProps[valuePropName] = values[name];
      dataBindProps[trigger] = this.handleValueChange;
    }
    if (validateTrigger !== 'ignore' && name) {
      dataBindProps[validateTrigger] =
        validateTrigger === trigger ? this.handleChangeAndValidate : this.handleValidate;
    }
    let status;
    if (name == null) {
      status = statusProps;
    } else if (validatings[name]) {
      status = 'validating';
    } else if (errors[name] && errors[name].length > 0) {
      status = 'error';
    } else if (errors[name] && errors[name].length === 0) {
      status = 'success';
    }
    return render({
      ...other,
      id: `easy-form-${name || Math.floor(Math.random() * 10000)}`,
      name,
      dataBindProps,
      trigger,
      status,
      onValidate: this.handleValidate,
      onValueChange: this.handleValueChange,
      onValueChangeAndValidate: this.handleChangeAndValidate,
      error: name ? errors[name] : error,
      validating: name ? validatings[name] : status === 'validating',
    });
  }
}
export default connect(Field);



// WEBPACK FOOTER //
// ./src/easyForm/ValidationField.jsx